import { makeAuthenticatedRequest } from './authService';

// ========================================
// Types for Thinking Framework Service
// ========================================

export interface ThinkingFrameworkSession {
  session_id: string;
  user_id: number;
  framework_type: string;
  title: string;
  framework_data: any;
  created_at: string;
  updated_at: string;
}

export interface SWOTData {
  strengths: string[];
  weaknesses: string[];
  opportunities: string[];
  threats: string[];
}

export interface CreateSessionParams {
  framework_type: string;
  title: string;
  framework_data: any;
}

export interface UpdateSessionParams {
  title?: string;
  framework_data?: any;
}

// ========================================
// Thinking Framework Service
// ========================================

export const thinkingFrameworkService = {
  /**
   * Get all sessions for the user (optionally filtered by framework type)
   */
  async getSessions(frameworkType?: string): Promise<ThinkingFrameworkSession[]> {
    try {
      const url = frameworkType 
        ? `/thinking-frameworks?framework_type=${frameworkType}`
        : '/thinking-frameworks';
      
      const response = await makeAuthenticatedRequest(url);

      if (!response.success) {
        throw new Error(response.message || 'Failed to get sessions');
      }

      return response.sessions || [];
    } catch (error: any) {
      console.error('Error getting thinking framework sessions:', error);
      throw error;
    }
  },

  /**
   * Get a specific session by ID
   */
  async getSession(sessionId: string): Promise<ThinkingFrameworkSession> {
    try {
      const response = await makeAuthenticatedRequest(`/thinking-frameworks/${sessionId}`);

      if (!response.success) {
        throw new Error(response.message || 'Failed to get session');
      }

      return response.session;
    } catch (error: any) {
      console.error('Error getting thinking framework session:', error);
      throw error;
    }
  },

  /**
   * Create a new session
   */
  async createSession(params: CreateSessionParams): Promise<ThinkingFrameworkSession> {
    try {
      const response = await makeAuthenticatedRequest('/thinking-frameworks', {
        method: 'POST',
        body: JSON.stringify(params)
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to create session');
      }

      return response.session;
    } catch (error: any) {
      console.error('Error creating thinking framework session:', error);
      throw error;
    }
  },

  /**
   * Update an existing session
   */
  async updateSession(sessionId: string, params: UpdateSessionParams): Promise<ThinkingFrameworkSession> {
    try {
      const response = await makeAuthenticatedRequest(`/thinking-frameworks/${sessionId}`, {
        method: 'PUT',
        body: JSON.stringify(params)
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to update session');
      }

      return response.session;
    } catch (error: any) {
      console.error('Error updating thinking framework session:', error);
      throw error;
    }
  },

  /**
   * Delete a session
   */
  async deleteSession(sessionId: string): Promise<void> {
    try {
      const response = await makeAuthenticatedRequest(`/thinking-frameworks/${sessionId}`, {
        method: 'DELETE'
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to delete session');
      }
    } catch (error: any) {
      console.error('Error deleting thinking framework session:', error);
      throw error;
    }
  }
};

export default thinkingFrameworkService;

